import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, FormControl, MenuItem, Select } from '@mui/material';
import StateSelector from '../Button/StateSelector';
import ColorPickerPopover from '../ColorPickerObjects';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import { updateImageElement } from '../../../redux/TabTile/TabActions';
import { RootState } from '../../../redux';
import { ImageElementType } from '../../../redux/TabTile/TabStateInterfaces';

type StateType = 'default' | 'hover';

// Default colors for the properties
const DEFAULT_COLORS = {
  backgroundColor: '#ffffff',
  borderColor: '#808080',
  backgroundColorHover: '#f5f5f5',
  borderColorHover: '#666666',
};

interface PropertiesProps {
  iconColor?: string;
  backgroundColor?: string;
}

const Properties: React.FC<PropertiesProps> = ({ iconColor, backgroundColor }) => {
  const dispatch = useDispatch();
  const [containerBgColorAnchor, setContainerBgColorAnchor] = useState<HTMLElement | null>(null);
  const [containerBorderColorAnchor, setContainerBorderColorAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [activeState, setActiveState] = useState<StateType>('default');

  const activeTabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);
  const { selectedImageId, imageElements } = useSelector((state: RootState) => {
    const tabData = state.tabState.tabs[activeTabId]?.dashboardObjects || {};
    return {
      selectedImageId: tabData.selectedImageId || null,
      imageElements: tabData.imageElements || [],
    };
  });

  const selectedImage = imageElements.find((img: ImageElementType) => img.id === selectedImageId);

  const [tooltipText, setTooltipText] = useState(
    selectedImage?.tooltip?.text || 'Press CTRL + click to follow link',
  );
  const [tooltipEnabled, setTooltipEnabled] = useState(selectedImage?.tooltip?.enabled ?? true);

  if (!selectedImage) return <div className="optionsInfo">No element selected</div>;

  const currentContainerProps = (() => {
    const props = selectedImage.containerProps;
    if (!props) return {};

    if ('default' in props) {
      return activeState === 'hover' ? props.hover || props.default : props.default;
    }

    return activeState === 'default' ? props : {};
  })();

  const updateImage = (updates: Partial<ImageElementType>) => {
    if (selectedImageId) {
      dispatch(
        updateImageElement(activeTabId, selectedImageId, {
          ...updates,
          tooltip: {
            enabled: tooltipEnabled,
            text: tooltipText,
            ...(updates.tooltip || {}),
          },
        }),
      );
    }
  };

  const updateContainerProps = (updates: Partial<ImageElementType['containerProps']>) => {
    let newContainerProps: any = {};
    const existing = selectedImage.containerProps;

    if (existing && 'default' in existing) {
      newContainerProps = { ...existing };
      if (activeState === 'default') {
        newContainerProps.default = { ...newContainerProps.default, ...updates };
      } else {
        newContainerProps.hover = {
          ...(newContainerProps.hover || newContainerProps.default),
          ...updates,
        };
      }
    } else {
      const flat = existing || {};
      newContainerProps =
        activeState === 'default'
          ? { ...flat, ...updates }
          : { default: { ...flat }, hover: updates };
    }

    updateImage({ containerProps: newContainerProps });
  };

  const resetImageProperties = () => {
    if (!selectedImageId) return;
    dispatch(
      updateImageElement(activeTabId, selectedImageId, {
        backgroundColor: undefined,
        backgroundAlpha: 100,
        borderRadius: 12,
        containerProps: { borderRadius: 12 },
        tooltip: { enabled: true, text: 'Press CTRL + click to follow link' },
      }),
    );
    setTooltipEnabled(true);
    setTooltipText('Press CNTL + click to follow link');
  };

  const hasModifiedValues = () => {
    if (!selectedImage) return false;

    const tooltipModified =
      selectedImage.tooltip?.enabled !== true ||
      selectedImage.tooltip?.text !== 'Press CTRL + click to follow link';
    const bgColorModified = selectedImage.backgroundColor !== '#ffffff';
    const alphaModified = selectedImage.backgroundAlpha !== 100;

    const containerPropsModified =
      selectedImage.containerProps !== undefined || selectedImage.borderRadius !== 12;

    return tooltipModified || bgColorModified || alphaModified || containerPropsModified;
  };

  const getDefaultColor = (type: 'background' | 'border') => {
    const colorKey = type === 'background' ? 'backgroundColor' : 'borderColor';

    if (selectedImage.containerProps) {
      const { containerProps } = selectedImage;

      if ('default' in containerProps) {
        const stateProps =
          activeState === 'hover' ? containerProps.hover || {} : containerProps.default || {};
        return (
          stateProps[colorKey] ||
          containerProps.default?.[colorKey] || // fallback to default state's value
          DEFAULT_COLORS[`${colorKey}${activeState === 'hover' ? 'Hover' : ''}`]
        );
      } else {
        return (
          containerProps[colorKey] ||
          DEFAULT_COLORS[`${colorKey}${activeState === 'hover' ? 'Hover' : ''}`]
        );
      }
    }

    return DEFAULT_COLORS[`${colorKey}${activeState === 'hover' ? 'Hover' : ''}`];
  };

  return (
    <div className="optionsInfo">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {/* State Selector */}
        <StateSelector
          activeState={activeState}
          setActiveState={setActiveState}
          iconColor={iconColor}
          backgroundColor={backgroundColor}
        />

        {/* Tooltip */}
        <div style={{ borderTop: '1px solid lightgray' }}>
          <div className="optionDescription" style={{ marginBottom: 3, color: iconColor }}>
            Tooltip
            <input
              type="checkbox"
              checked={tooltipEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                setTooltipEnabled(enabled);
                updateImage({ tooltip: { enabled, text: tooltipText } });
              }}
              style={{ marginLeft: 8, cursor: 'pointer', accentColor: '#808080' }}
            />
          </div>
          <input
            type="text"
            value={tooltipText}
            disabled={!tooltipEnabled}
            onChange={(e) => {
              const text = e.target.value;
              setTooltipText(text);
              updateImage({ tooltip: { enabled: tooltipEnabled, text } });
            }}
            placeholder={
              activeState === 'default'
                ? 'Press CNTL + click to follow link'
                : 'Press click to follow link'
            }
            style={{
              width: '95%',
              padding: '8px 10px',
              fontSize: 12,
              border: '1px solid #ccc',
              height: 25,
              borderRadius: 2,
              backgroundColor: tooltipEnabled ? 'white' : '#f5f5f5',
              color: tooltipEnabled ? 'inherit' : '#a0a0a0',
            }}
          />
        </div>

        {/* Background Color */}
        <div
          className="optionDescription"
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            color: iconColor,
          }}
        >
          Background Color
          <div
            style={{
              marginTop: 3,
              width: 100,
              height: '1.25rem',
              backgroundColor: getDefaultColor('background'),
              border: '2px solid darkgray',
              borderRadius: 4,
              cursor: 'pointer',
            }}
            onClick={(e) => setContainerBgColorAnchor(e.currentTarget)}
          />
        </div>
        <ColorPickerPopover
          isOpen={Boolean(containerBgColorAnchor)}
          anchorEl={containerBgColorAnchor}
          onClose={() => setContainerBgColorAnchor(null)}
          currentColor={getDefaultColor('background')}
          onColorChange={(newColor) => updateContainerProps({ backgroundColor: newColor })}
        />

        {/* Border Color */}
        <div
          className="optionDescription"
          style={{
            marginTop: 6,
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            color: iconColor,
          }}
        >
          Border Color
          <div
            style={{
              marginTop: 3,
              width: 100,
              height: '1.25rem',
              backgroundColor: getDefaultColor('border'),
              border: '2px solid darkgray',
              borderRadius: 4,
              cursor: 'pointer',
            }}
            onClick={(e) => setContainerBorderColorAnchor(e.currentTarget)}
          />
        </div>
        <ColorPickerPopover
          isOpen={Boolean(containerBorderColorAnchor)}
          anchorEl={containerBorderColorAnchor}
          onClose={() => setContainerBorderColorAnchor(null)}
          currentColor={getDefaultColor('border')}
          onColorChange={(newColor) => updateContainerProps({ borderColor: newColor })}
        />

        {/* Sliders */}

        <div className="optionDescription" style={{ marginTop: 6, color: iconColor }}>
          Padding Top
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentContainerProps.paddingTop || 0}
          sliderMinMax={{ min: 0, max: 30, step: 1 }}
          changeValue={(val) => updateContainerProps({ paddingTop: val })}
        />

        <div className="optionDescription" style={{ marginTop: 6, color: iconColor }}>
          Padding Right
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentContainerProps.paddingRight || 0}
          sliderMinMax={{ min: 0, max: 30, step: 1 }}
          changeValue={(val) => updateContainerProps({ paddingRight: val })}
        />

        <div className="optionDescription" style={{ marginTop: 6, color: iconColor }}>
          Padding Bottom
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentContainerProps.paddingBottom || 0}
          sliderMinMax={{ min: 0, max: 30, step: 1 }}
          changeValue={(val) => updateContainerProps({ paddingBottom: val })}
        />

        <div className="optionDescription" style={{ marginTop: 6, color: iconColor }}>
          Padding Left
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentContainerProps.paddingLeft || 0}
          sliderMinMax={{ min: 0, max: 30, step: 1 }}
          changeValue={(val) => updateContainerProps({ paddingLeft: val })}
        />

        <div className="optionDescription" style={{ marginTop: 12, color: iconColor }}>
          Border Radius
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentContainerProps.borderRadius ?? selectedImage.borderRadius ?? 12}
          sliderMinMax={{ min: 0, max: 50, step: 1 }}
          changeValue={(val) => updateContainerProps({ borderRadius: val })}
        />

        {/* Rotation */}
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <div className="optionDescription" style={{ marginBottom: 3, color: iconColor }}>
            Rotation
          </div>
          <Select
            value={currentContainerProps.rotation || 0}
            onChange={(e) => updateContainerProps({ rotation: Number(e.target.value) })}
            sx={{
              fontSize: 12,
              backgroundColor: 'white',
              height: '1.5rem',
              color: '#404040',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2bb9bb',
              },
            }}
          >
            {[0, 90, 180, 270, -90].map((angle) => (
              <MenuItem key={angle} value={angle} sx={{ fontSize: 12 }}>
                {angle}°
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Reset Button */}
        <Button
          onClick={resetImageProperties}
          disabled={!hasModifiedValues()}
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            mb: 1,
            backgroundColor: 'transparent',
            color: '#2BB9BB',
            border: '1px solid #2BB9BB',
            boxShadow: 'none',
            '&:hover:not(:disabled)': {
              backgroundColor: '#229a9a',
              color: '#ffffff',
              borderColor: '#808080',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
              borderColor: '#e0e0e0',
            },
          }}
        >
          Reset Properties
        </Button>
      </Box>
    </div>
  );
};

export default Properties;
